import { titleize } from '@ltvco/refresh-lib/utils';

import {
  MAX_VISIBLE_ALIASES,
  MAX_VISIBLE_RELATIVES,
  MONTHS,
} from 'components/DeepSearch/constants';

export function hasValue(value: string): boolean {
  const trimmed = value.trim();
  return Boolean(trimmed) && trimmed.toLowerCase() !== 'unknown';
}

export type StatusSeverity = 'error' | 'warning' | 'success';

export interface StatusConfig {
  severity: StatusSeverity;
  isWarning: boolean;
}

export function getLeakedConfig(value: string): StatusConfig {
  const isLeaked = value.toLowerCase().includes('yes');
  return {
    severity: isLeaked ? 'error' : 'success',
    isWarning: isLeaked,
  };
}

/**
 * User Activity measures *legitimate* phone activity, so higher activity = lower
 * risk (mirrors the lib's getActivityStatusV2 with isPositive=false).
 * "none"/"unknown" are not positive signals, so they stay out of the green state.
 */
export function getActivityConfig(value: string): StatusConfig {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'high') {
    return { severity: 'success', isWarning: false };
  }
  if (normalized === 'low') {
    return { severity: 'error', isWarning: true };
  }
  return { severity: 'warning', isWarning: true };
}

export function getDncConfig(value: string): StatusConfig {
  const isNotOnList = value.toLowerCase().includes('not');
  return {
    severity: isNotOnList ? 'success' : 'error',
    isWarning: !isNotOnList,
  };
}

export type FraudRiskLevel = 'high' | 'medium' | 'low' | 'unknown';

export interface FraudRiskConfig {
  label: string;
  severity: StatusSeverity;
  description: string;
}

const FRAUD_RISK_CONFIG: Record<'low' | 'medium' | 'high', FraudRiskConfig> = {
  low: {
    label: 'Low Spam Risk',
    severity: 'success',
    description:
      "This number has a very low spam complaint value and hasn't been reported for recent abuse. We can't guarantee whether or not this phone number is actually being used to spam or commit fraudulent activities.",
  },
  medium: {
    label: 'Elevated Fraud Risk',
    severity: 'warning',
    description:
      'This number has been flagged as a potentially risky phone number. Some factors that can contribute to risky phone numbers include higher spam complaint values, higher levels of user complaints, any reported recent abuse, and/or a high number of user comments.',
  },
  high: {
    label: 'High Fraud Risk',
    severity: 'error',
    description:
      'This number has been flagged as a potentially very risky phone number. Some factors that can contribute to higher risk phone numbers include higher spam complaint values, higher levels of user complaints, any reported recent abuse, and/or a high number of user comments.',
  },
};

/** Returns null for "unknown" so the chip + description are hidden. */
export function getFraudRiskConfig(
  level: FraudRiskLevel
): FraudRiskConfig | null {
  if (level === 'unknown') {
    return null;
  }
  return FRAUD_RISK_CONFIG[level] ?? null;
}

export function formatMileage(value: string): string {
  if (!value.trim()) {
    return value;
  }
  const trimmed = value.trim();
  if (trimmed.toLowerCase().endsWith('mi')) {
    return trimmed;
  }
  const num = Number(trimmed.replace(/,/g, ''));
  if (Number.isFinite(num)) {
    return `${num.toLocaleString('en-US')} mi`;
  }
  return trimmed;
}

export function formatCurrency(value: string): string {
  if (!value.trim()) {
    return value;
  }
  if (value.trimStart().startsWith('$')) {
    return value;
  }
  const num = Number(value.replace(/,/g, ''));
  if (Number.isFinite(num)) {
    return `$${num.toLocaleString('en-US')}`;
  }
  return value;
}

export function formatDob(dob: string): string {
  if (!dob) {
    return '';
  }
  const [year, month] = dob.split('-');
  if (!year) {
    return dob;
  }
  if (!month) {
    return year;
  }
  const monthName = MONTHS[parseInt(month, 10) - 1];
  return monthName ? `${monthName} ${year}` : year;
}

export function formatDod(dod: string): string {
  if (!dod) {
    return '';
  }
  const parts = dod.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${month}/${day}/${year}`;
  }
  const [year, month] = parts;
  if (!year) {
    return dod;
  }
  if (!month) {
    return year;
  }
  const monthName = MONTHS[parseInt(month, 10) - 1];
  return monthName ? `${monthName} ${year}` : year;
}

export function calculateAge(dob: string, dod: string): number | null {
  if (!dob || !dod) {
    return null;
  }
  const [dobYear, dobMonth] = dob.split('-').map(Number);
  const dodParts = dod.split('-').map(Number);
  if (!dobYear || !dodParts[0]) {
    return null;
  }
  const dodYear = dodParts[0];
  const dodMonth = dodParts[1] ?? 12;
  let age = dodYear - dobYear;
  if (dobMonth && dodMonth < dobMonth) {
    age--;
  }
  return age > 0 ? age : null;
}

export function titleCase(str: string): string {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatMonthYear(dateStr: string): string {
  const [year, month] = dateStr.split('-');
  if (!year) {
    return dateStr;
  }
  const monthName = MONTHS[parseInt(month, 10) - 1];
  return monthName ? `${monthName.slice(0, 3)} ${year}` : year;
}

export function formatLongDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-');
  if (!year) {
    return dateStr;
  }
  const monthName = MONTHS[parseInt(month, 10) - 1];
  if (!monthName) {
    return year;
  }
  if (!day) {
    return `${monthName.slice(0, 3)} ${year}`;
  }
  return `${monthName.slice(0, 3)} ${parseInt(day, 10)}, ${year}`;
}

export function formatPhoneNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
}

export const normalizePhoneDigits = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  return digits;
};

const PHONE_LINK_TYPE_LABELS: Record<string, string> = {
  l: 'Landline',
  m: 'Mobile',
  w: 'Wireless',
  v: 'VoIP',
  p: 'Pager',
  f: 'Fax',
  h: 'Home',
  o: 'Other',
  c: 'Commercial',
  d: 'Digital',
};

export const formatPhoneLinkType = (type: string): string => {
  const trimmed = type.trim();
  if (!trimmed) {
    return '';
  }

  if (trimmed.length === 1) {
    return PHONE_LINK_TYPE_LABELS[trimmed.toLowerCase()] ?? '';
  }

  return titleCase(trimmed);
};

const TIMEZONE_LABELS: Record<string, string> = {
  'America/New_York': 'Eastern Time',
  'America/Chicago': 'Central Time',
  'America/Denver': 'Mountain Time',
  'America/Los_Angeles': 'Pacific Time',
  'America/Anchorage': 'Alaska Time',
  'Pacific/Honolulu': 'Hawaii Time',
  'America/Phoenix': 'Mountain Time (AZ)',
  'America/Detroit': 'Eastern Time',
  'America/Indiana/Indianapolis': 'Eastern Time',
};

export function formatTimezone(tz: string): string {
  return TIMEZONE_LABELS[tz] ?? tz;
}

export function formatAliases(aliases: string[]): string {
  if (!aliases.length) {
    return '';
  }
  const visible = aliases.slice(0, MAX_VISIBLE_ALIASES);
  const extra = aliases.length - MAX_VISIBLE_ALIASES;
  const parts = visible.map((a) => titleize(a.toLowerCase()));
  const joined = parts.join(', ');
  return extra > 0 ? `${joined} +${extra} more` : joined;
}

export function formatRelatives(
  relatives: { name: string; type: string }[],
  options?: { titleizeParts?: boolean }
): string {
  if (!relatives.length) {
    return '';
  }
  const visible = relatives.slice(0, MAX_VISIBLE_RELATIVES);
  const extra = relatives.length - MAX_VISIBLE_RELATIVES;
  const parts = visible.map((r) =>
    options?.titleizeParts
      ? `${titleize(r.name.toLowerCase())} - ${titleize(r.type.toLowerCase())}`
      : `${r.name} - ${r.type}`
  );
  const joined = parts.join(' • ');
  return extra > 0 ? `${joined} + ${extra}` : joined;
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .filter((word) => word.length > 0)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function formatPropertyAddress(data: {
  address: string | null | undefined;
  city: string | null | undefined;
  state: string | null | undefined;
  zip: string | null | undefined;
}): { streetLine: string; locationLine: string } {
  const address = data.address ?? '';
  const city = data.city ?? '';
  const state = data.state ?? '';
  const zip = data.zip ?? '';

  const cityState = [city, state].filter(hasValue).join(', ');
  const locationLine = [cityState, zip].filter(hasValue).join(' ');

  let streetLine = address;

  if (hasValue(state) && hasValue(zip)) {
    const stateZipTail = `, ${state} ${zip}`;
    if (streetLine.endsWith(stateZipTail)) {
      streetLine = streetLine.slice(0, -stateZipTail.length).trim();
    }
  }

  if (hasValue(city) && hasValue(locationLine)) {
    const cityTailPatterns = [` ${city}`, `, ${city}`];
    for (const pattern of cityTailPatterns) {
      if (streetLine.endsWith(pattern)) {
        streetLine = streetLine.slice(0, -pattern.length).trim();
        break;
      }
    }
  }

  return { streetLine, locationLine };
}
